import React, { useState } from "react";
import styles from "./styles/AssetDashboard.module.css";

/*
  The chapter picker: which sections of the report go into the PDF.

  It stands between the "Download Financial Report" button and the capture. The
  button used to produce the file directly; now it opens this, and the file is
  produced from here once the reader has said what belongs in it.

  Everything is on to begin with, so pressing Generate twice — once to open,
  once to confirm — gives exactly the document the button gave before.

  ---- what a switch does, and does not, do ----

  Turning one off removes that section from the PDF. It does not renumber the
  rest: chapter 04 is still 04 with 03 left out. The numbers are names, they are
  what this list shows, and a document whose chapter numbers disagree with the
  list used to choose them would be worse than one with a gap in the sequence.

  The list is built by the page, in the report's own order, and includes one
  entry per asset-type section the account actually has — so it is as long as
  the report is, not as long as the design's mock-up.
*/
function SectionSelector({
  sections,
  title,
  intro,
  noLabel,
  yesLabel,
  closeLabel,
  generateLabel,
  busyLabel,
  busy,
  onClose,
  onGenerate,
}) {
  // Every chapter starts included; the reader takes away rather than builds up.
  const [off, setOff] = useState(() => new Set());

  const toggle = (key) =>
    setOff((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const chosen = sections.filter((section) => !off.has(section.key));

  return (
    <div
      className={styles.ss_backdrop}
      onClick={busy ? undefined : onClose}
      role="presentation"
    >
      <div
        className={styles.ss_modal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className={styles.ss_title}>{title}</h3>
        <button
          type="button"
          className={styles.ss_close}
          aria-label={closeLabel}
          onClick={onClose}
          disabled={busy}
        />

        <div className={styles.ss_intro}>{intro}</div>

        <ul className={styles.ss_list}>
          {sections.map((section) => {
            const on = !off.has(section.key);
            return (
              <li key={section.key} className={styles.ss_row}>
                {/*
                  The label wraps the switch, so the chapter's name is its hit
                  target too — the switch alone is 24px of a 522px-wide row.
                */}
                <label className={styles.ss_label}>
                  <span className={styles.ss_name}>
                    <span className={styles.ss_num}>
                      {String(section.number).padStart(2, "0")}
                    </span>
                    {" - "}
                    {section.title}
                  </span>

                  <span className={styles.ss_switch_wrap}>
                    <span className={styles.ss_side}>{noLabel}</span>
                    {/*
                      A real checkbox under the drawn switch: it carries the
                      state, the keyboard and the screen reader, and only its
                      appearance is replaced.
                    */}
                    <input
                      type="checkbox"
                      className={styles.ss_input}
                      checked={on}
                      disabled={busy}
                      onChange={() => toggle(section.key)}
                    />
                    <span className={styles.ss_switch} aria-hidden="true">
                      <span className={styles.ss_knob} />
                    </span>
                    <span className={styles.ss_side}>{yesLabel}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className={styles.ss_generate}
          /*
            Nothing chosen is not a report. The header card would still be
            captured on its own, which is a file that answers no question.
          */
          disabled={busy || chosen.length === 0}
          onClick={() => onGenerate(Array.from(off))}
        >
          {busy ? busyLabel : generateLabel}
        </button>
      </div>
    </div>
  );
}

export default SectionSelector;
